/**
 * PostcardLayer — Sprint 4A mount point (app-wide while a trip is active).
 * Listens for a gem surfacing and, the moment the LAST star of the active trip's
 * city lights, reveals the city postcard — quietly, once. The digital unlock is
 * free, instant and local; no network. The artefact is the reward, never points.
 *
 * Kept separate from ProximityLayer so the whisper and the reward don't fight for
 * the same surface or lifecycle.
 */
import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import storageManager from '../utils/storageManager';
import {
  evaluateCityCompletion,
  getUnlockedPostcards,
  buildRedeemUrl,
  POSTCARD_PHYSICAL,
} from '../utils/postcardManager';
import PostcardCard from './PostcardCard.jsx';
import theme from '../styles/theme';

export default function PostcardLayer({ hasActiveTrip }) {
  const { t, i18n } = useTranslation('common');
  const [unlocked, setUnlocked] = useState(null); // { cityId, city, country, code } | null
  const busy = useRef(false);

  useEffect(() => {
    if (!hasActiveTrip) return;

    const check = async () => {
      if (busy.current) return;
      busy.current = true;
      try {
        const trip = await storageManager.getActiveTrip().catch(() => null);
        if (!trip) return;
        const res = await evaluateCityCompletion(trip);
        if (res.newlyUnlocked) {
          // Pull the freshly-minted card (with its redeem code) for the moment.
          const card = (await getUnlockedPostcards().catch(() => []))
            .find(c => c.cityId === res.cityId);
          setUnlocked({ cityId: res.cityId, city: res.city, country: res.country, code: card?.code });
          try {
            window.dispatchEvent(new CustomEvent('wayfare-postcard-unlocked', {
              detail: { cityId: res.cityId },
            }));
          } catch { /* non-fatal */ }
        }
      } finally {
        busy.current = false;
      }
    };

    window.addEventListener('wayfare-gem-surfaced', check);
    // Also check once on mount — a trip may have completed cross-trip already.
    check();
    return () => window.removeEventListener('wayfare-gem-surfaced', check);
  }, [hasActiveTrip]);

  if (!unlocked) return null;

  return (
    <div style={S.scrim} role="dialog" aria-modal="true" onClick={() => setUnlocked(null)}>
      <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
        <div style={S.eyebrow}>{t('postcard.unlockedEyebrow')}</div>
        <h2 style={S.title}>{t('postcard.unlockedTitle', { city: unlocked.city })}</h2>
        <PostcardCard city={unlocked.city} country={unlocked.country} />
        <p style={S.body}>{t('postcard.unlockedBody')}</p>
        {POSTCARD_PHYSICAL === 'link' && unlocked.code && (
          <a href={buildRedeemUrl(unlocked, i18n.language)} target="_blank" rel="noopener noreferrer"
            style={S.link} onClick={() => setUnlocked(null)}>
            {t('postcard.physicalLink')}
          </a>
        )}
        <button type="button" style={S.btn} onClick={() => setUnlocked(null)}>
          {t('postcard.unlockedDismiss')}
        </button>
      </div>
    </div>
  );
}

const S = {
  scrim: {
    position: 'fixed', inset: 0, zIndex: 1400,
    background: 'rgba(26,51,64,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '20px',
    animation: 'wf-whisper-in 280ms ease-out',
  },
  sheet: {
    width: '100%', maxWidth: 420,
    background: theme.card,
    borderRadius: 18,
    padding: '22px 22px 18px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.30)',
  },
  eyebrow: {
    fontSize: 11, letterSpacing: '0.12em', textTransform: 'uppercase',
    color: theme.terracottaDark, fontWeight: 700, marginBottom: 6, textAlign: 'center',
  },
  title: {
    margin: '0 0 14px', textAlign: 'center',
    fontSize: 22, fontWeight: 800, color: theme.brownDark,
    fontFamily: "'Noto Serif', serif",
  },
  body: {
    margin: '14px 0 16px', textAlign: 'center',
    fontSize: 14, lineHeight: 1.5, color: theme.brown, fontStyle: 'italic',
  },
  link: {
    display: 'block', textAlign: 'center', textDecoration: 'none',
    padding: '12px 0', marginBottom: 8,
    background: theme.terracotta, color: '#fff',
    borderRadius: 10, fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
  btn: {
    width: '100%', padding: '12px 0',
    background: 'transparent', color: theme.blueDeep,
    border: 'none', borderRadius: 10,
    fontSize: 15, fontWeight: 700, cursor: 'pointer',
  },
};
